import { GridOptions } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { memo, useMemo } from "react";

import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { gridTheme } from "@src/components/MainApp/SharedProductPicker/gridTheme";
import { createProductColumns } from "@src/components/MainApp/SharedProductPicker/list/productListColumns";
import { ProductListVariant } from "@src/components/MainApp/SharedProductPicker/list/ProductListVariant";
import { SelectAllProgressBar } from "@src/components/MainApp/SharedProductPicker/list/SelectAllProgressBar";
import { useProductPickerContext } from "@src/components/MainApp/SharedProductPicker/ProductPickerContext";
import { ProductRenderParams } from "@src/components/MainApp/SharedProductPicker/types";
import { useSelector } from "@src/store";
import {
  getProductPickerData,
  isProductPickerSelectingAllProducts,
  PRODUCT_PICKER_PAGE_SIZE,
} from "@src/store/slices/productPicker";
import {
  GetProductPickerInfo,
  ProductPickerProduct,
} from "@src/store/slices/productPicker/types";
import { buildsTestCart } from "@src/store/slices/productPicker/util/productPickerPolicy";

interface ProductListProps {
  getProductPickerInfo?: GetProductPickerInfo;
  isView?: boolean;
}

export const ProductList = memo((props: ProductListProps) => {
  const { dataSource, loadVariants } = useProductPickerContext();
  const columnDefs = useColumnDefs(props, loadVariants);
  const gridOptions = useGridOptions();
  const loading = useSelector(isProductPickerSelectingAllProducts);
  const pickerType = useSelector(getProductPickerData)?.type;

  return (
    <Container sx={{ height: "435px" }}>
      <AgGridReact<ProductPickerProduct>
        serverSideDatasource={dataSource}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        loading={loading}
        loadingOverlayComponent={SelectAllProgressBar}
        onFirstDataRendered={({ api }) => {
          // A test cart shows every multi-variant product expanded by default
          if (!buildsTestCart(pickerType)) {
            return;
          }
          api.forEachNode((node) => {
            if (node.master) {
              node.setExpanded(true);
            }
          });
        }}
      />
    </Container>
  );
});

const Container = styled(Box)({
  marginRight: "16px",
  marginLeft: "16px",
  marginTop: "8px",
  flex: 1,
  "& .ag-body-viewport ": { overscrollBehavior: "contain" },
  "& .ag-root-wrapper": { borderRadius: 0 },
});

function useGridOptions() {
  return useMemo(() => {
    const gridOptions: GridOptions<ProductPickerProduct> = {
      theme: gridTheme,
      rowModelType: "serverSide",
      suppressAggFuncInHeader: true,
      headerHeight: 40,
      rowHeight: 60,
      cacheBlockSize: PRODUCT_PICKER_PAGE_SIZE,
      rowSelection: { mode: "singleRow", checkboxes: false },
      getRowId: (params) => params.data.id,
      animateRows: false,
      cellSelection: false,
      stopEditingWhenCellsLoseFocus: true,
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 25,
      suppressCellFocus: true,
      suppressMovableColumns: true,
      suppressContextMenu: true,
      suppressMenuHide: false,
      suppressAutoSize: true,
      enableBrowserTooltips: true,
      suppressHorizontalScroll: true,
      masterDetail: true,
      detailCellRenderer: ProductListVariant,
      detailRowAutoHeight: true,
      isRowMaster: (product) => {
        return !!(product.variants && product.variants.length > 1);
      },
    };
    return gridOptions;
  }, []);
}

function useColumnDefs(
  { getProductPickerInfo, isView }: ProductListProps,
  loadVariants?: boolean
) {
  return useMemo(() => {
    const cellRendererParams: ProductRenderParams = {
      getProductPickerInfo,
      isView,
    };

    return createProductColumns({
      cellRendererParams,
      loadVariants,
    });
  }, [getProductPickerInfo, isView, loadVariants]);
}
